"use client";

/**
 * TenantBrandingContext + hooks — home in lib (rank 0).
 *
 * Extracted from src/components/ui/providers/TenantBrandingProvider.tsx so that
 * atoms/molecules/hooks can import the hooks without layer-graph violations
 * (molecule→provider was rank 2→3, violating no-upward rule).
 *
 * TenantBrandingProvider.tsx imports the context from here and wraps it;
 * all other consumers import from here directly.
 */

import type { BrandConfig } from "@/lib/theme/branding";
import { createContext, useContext } from "react";

interface TenantBrandingContextValue {
    brand: BrandConfig;
    tenantName: string;
    tenantSlug: string;
}

export const TenantBrandingContext = createContext<TenantBrandingContextValue | null>(null);

/**
 * Hook to access tenant branding context.
 * Must be used within a TenantBrandingProvider.
 */
export function useTenantBranding(): TenantBrandingContextValue {
    const context = useContext(TenantBrandingContext);
    if (!context) {
        throw new Error("useTenantBranding must be used within TenantBrandingProvider");
    }
    return context;
}

/**
 * Optional hook that returns null if outside provider (for components that may
 * render both inside and outside branded contexts).
 */
export function useTenantBrandingOptional(): TenantBrandingContextValue | null {
    return useContext(TenantBrandingContext);
}
